import {
  buildOrderCustomerDocData,
  escapeHtml,
  renderOrderCustomerDocHtml,
  type OrderCustomerDocData,
} from './orderCustomerDoc';
import { loadOrder } from './exportOrderCustomerDoc';
import type { TenantOrder } from '@/mocks/orders';

/**
 * TCR-2506 — in tài liệu "ĐƠN HÀNG" theo LAYOUT WORD MẪU (khung khách đưa)
 * cho đơn sỉ GT/MT.
 *
 * Cố tình KHÔNG dùng html2canvas (ảnh raster): chữ phải là vector để in A4
 * sắc nét, đúng như bản .docx mà khách nhận. Tài liệu được nạp vào iframe ẩn
 * rồi gọi `print()` — hộp thoại in của trình duyệt lo phần khổ giấy A4.
 *
 * Khác `printOrderReceipt` (hoá đơn nhiệt POS, chạy qua hardware agent):
 * đây là giấy tờ A4 cho khách tổ chức, KHÔNG gửi máy in nhiệt.
 */

/**
 * CSS riêng cho bản IN — đặt SAU style của tài liệu nên được ưu tiên (cùng
 * độ ưu tiên, style sau thắng). Bỏ khung/padding cố định 794px của bản chụp
 * ảnh để nội dung tràn đúng khổ A4 trong `@page`.
 */
const PRINT_STYLE = `
  @page { size: A4 portrait; margin: 14mm 15mm; }
  html, body { margin: 0; padding: 0; background: #fff; }
  .tcr-order-doc { width: auto; padding: 0; font-size: 12pt; line-height: 1.35; }
  .tcr-order-doc h1 { font-size: 16pt; margin: 0 0 16pt; }
  .tcr-order-doc .items th, .tcr-order-doc .items td { padding: 5pt 4pt; }
  .tcr-order-doc .sign td { padding-bottom: 56pt; }
`;

/**
 * Trang HTML hoàn chỉnh để in (khổ A4) — thuần, test được không cần DOM.
 */
export function buildOrderCustomerDocPrintHtml(data: OrderCustomerDocData): string {
  return `<!doctype html>
<html lang="vi"><head><meta charset="utf-8"><title>Đơn hàng ${escapeHtml(data.orderNumber)}</title></head>
<body>${renderOrderCustomerDocHtml(data)}<style>${PRINT_STYLE}</style></body></html>`;
}

/**
 * Thời gian chờ tối đa để iframe nạp xong tài liệu (ms).
 *
 * Nếu quá thời gian này (iframe treo, không phát `load`) thì coi như in
 * thất bại — Promise KHÔNG được treo vô thời hạn, nếu không nút "In đơn hàng"
 * sẽ kẹt ở trạng thái loading và người dùng không thấy lỗi.
 */
export const PRINT_IFRAME_TIMEOUT_MS = 15_000;

/** Chờ hộp thoại in hiện rồi mới gỡ iframe (ms). */
const IFRAME_CLEANUP_DELAY_MS = 2_000;

/**
 * Nạp tài liệu vào iframe ẩn rồi gọi `print()`. Dọn iframe sau khi in
 * (hộp thoại in của trình duyệt chạy bất đồng bộ nên phải chờ một nhịp).
 *
 * Luôn LUÔN kết thúc Promise (resolve hoặc reject) — có `error` handler,
 * timeout, và kiểm tra `contentWindow`/`print` trước khi coi là thành công.
 */
function printHtmlInHiddenIframe(html: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);

    let settled = false;
    let loadTimeoutId: number | undefined;
    let cleanupTimerId: number | undefined;

    /** Huỷ timer chờ nạp tài liệu. */
    const clearLoadTimeout = () => {
      if (loadTimeoutId !== undefined) {
        window.clearTimeout(loadTimeoutId);
        loadTimeoutId = undefined;
      }
    };

    /** Gỡ iframe + huỷ các timer đang chờ. */
    const cleanup = () => {
      clearLoadTimeout();
      if (cleanupTimerId !== undefined) {
        window.clearTimeout(cleanupTimerId);
        cleanupTimerId = undefined;
      }
      iframe.remove();
    };

    /**
     * Chỉ kết thúc Promise MỘT lần — `timeout` / `load` / `error` có thể đến
     * gần nhau, không được resolve lần hai (Promise bỏ qua lần hai nhưng
     * cleanup/iframe sẽ bị gọi hai lần).
     */
    const settle = (done: () => void) => {
      if (settled) return;
      settled = true;
      done();
    };

    /** Báo lỗi + gỡ iframe. */
    const fail = (message: string) => {
      settle(() => {
        cleanup();
        reject(new Error(message));
      });
    };

    loadTimeoutId = window.setTimeout(() => {
      fail('Không tải được tài liệu để in (quá thời gian chờ). Vui lòng thử lại hoặc dùng chức năng "Xuất Word".');
    }, PRINT_IFRAME_TIMEOUT_MS);

    iframe.addEventListener('load', () => {
      if (settled) return;
      const contentWindow = iframe.contentWindow;
      if (!contentWindow) {
        fail('Không mở được cửa sổ in. Vui lòng thử lại hoặc dùng chức năng "Xuất Word".');
        return;
      }
      if (typeof contentWindow.print !== 'function') {
        fail('Trình duyệt không hỗ trợ in tài liệu này. Vui lòng dùng chức năng "Xuất Word".');
        return;
      }
      try {
        contentWindow.focus?.();
        // `__MOCK_PRINT__` — cờ chặn in trong môi trường test (jsdom không in
        // thật), cùng cách `printReceipt.ts` dùng.
        if (typeof window !== 'undefined' && !(window as unknown as { __MOCK_PRINT__?: boolean }).__MOCK_PRINT__) {
          contentWindow.print();
        }
      } catch (error) {
        fail(error instanceof Error ? error.message : 'Không thể mở hộp thoại in. Vui lòng thử lại.');
        return;
      }
      settle(() => {
        clearLoadTimeout();
        // Để hộp thoại in của trình duyệt kịp hiện rồi mới gỡ iframe.
        cleanupTimerId = window.setTimeout(cleanup, IFRAME_CLEANUP_DELAY_MS);
        resolve();
      });
    });

    iframe.addEventListener('error', () => {
      fail('Không tải được tài liệu để in. Vui lòng thử lại hoặc dùng chức năng "Xuất Word".');
    });

    iframe.srcdoc = html;
  });
}

/**
 * In tài liệu "ĐƠN HÀNG" (layout Word mẫu) cho một đơn sỉ GT/MT.
 *
 * Dùng `loadOrder` để lấy CHI TIẾT đơn (dòng danh sách thiếu
 * `service_lines`, `buyer_invoice_info` đầy đủ...) — cùng nguồn với luồng
 * xuất file, nên bản in và bản tải về không lệch số.
 */
export async function printOrderCustomerDoc(row: TenantOrder): Promise<void> {
  const order = await loadOrder(row);
  const data = buildOrderCustomerDocData(order);
  await printHtmlInHiddenIframe(buildOrderCustomerDocPrintHtml(data));
}
